import {
  IHealthScribeService,
  IHealthScribeRepository,
  IHealthScribeWebSocketProvider,
  IHealthScribeAudioProvider,
  IListTranscriptionsRequest,
  IListTranscriptionsResponse,
  IHealthScribeSession,
  ITranscriptionMessage,
  IAudioChunk,
  IS3UploadCompleteAudioRequest,
  IS3UploadCompleteAudioResponse,
  // V2 Types
  IStartRecordingV2Request,
  IStartRecordingV2Response,
  IStopRecordingV2Response,
  IGenerateNarrativeV2Response,
  ICompleteSessionV2Request,
  ICompleteSessionV2Response,
  ITranscriptionContext,
  IListSessionsV2Request,
  IListSessionsV2Response,
  // Status Types
  SessionStatus,
} from "../ports/HealthScribe";
import { mergeTranscriptions } from "../../../util/sequenceMerge";

// Configuration: Maximum number of recordings to keep in localStorage per storage key
// This sliding window mechanism prevents localStorage from becoming full
const MAX_RECORDINGS_PER_KEY = 100;

export const HealthScribeServiceFactory = ({
  healthScribeRepository,
  healthScribeWebSocketProvider,
  healthScribeAudioProvider,
}: {
  healthScribeRepository: IHealthScribeRepository;
  healthScribeWebSocketProvider: IHealthScribeWebSocketProvider;
  healthScribeAudioProvider: IHealthScribeAudioProvider;
}): IHealthScribeService => {
  let currentSession: IHealthScribeSession | null = null;

  // V2: Track current segment separately
  // SEGMENT ARCHITECTURE:
  // - A SESSION contains multiple SEGMENTS (recording periods)
  // - Each Start/Resume creates a NEW segment (segment #1, #2, #3, etc.)
  // - Each Pause/Complete closes the current segment
  // - Segments are like chapters in a book (Session = book, Segments = chapters)
  // - Example: Start → Record 5min → Pause (Segment #1 closed)
  //            Resume → Record 5min → Pause (Segment #2 closed)
  //            Resume → Record 5min → Complete (Segment #3 closed)
  // - All segments in a session are combined when generating narrative
  let currentSegmentData: {
    sessionId: string;
    segmentId: string;
    segmentNumber: number;
    transcriptions: any[];
    clinicId?: number;
    patientId?: number;
    visitNoteId?: number; // Added for localStorage fallback saves
    visitType?: string;
    context?: ITranscriptionContext;
  } | null = null;

  // Degraded mode: Buffer audio chunks when WebSocket unavailable
  // TODO test whether "Degraded Mode" does anything useful (computes transcripts from buffered audio?), and if not, remove it!
  let audioChunkBuffer: IAudioChunk[] = [];
  let isFlushingBuffer = false;
  let isDegradedMode = false;
  let lastReconnectAttempt = 0;
  const MAX_BUFFER_SIZE = 1000; // ~100 seconds of audio at 10 chunks/sec
  const RECONNECT_INTERVAL_MS = 1_000; // Try reconnection every 1 second
  let reconnectIntervalId: number | null = null;

  // Failed transcriptions retry buffer
  // If a real-time save fails, we buffer it and include it in the next save attempt
  let failedTranscriptionsBuffer: any[] = [];
  const MAX_FAILED_BUFFER_SIZE = 500; // Maximum failed transcriptions to buffer

  const sanitizeNarrative = (narrative?: string | null): string | undefined => {
    if (typeof narrative !== "string") return narrative ?? undefined;

    let sanitized = narrative;

    // 0.5) Replace malformed non-literal newlines with literal newlines
    sanitized = sanitized.replace(/\\n/g, "\n");

    // 0.6) <br> → newline
    sanitized = sanitized.replace(/<br\s*\/?>/gi, "\n");

    // 1) Remove leading whitespace
    sanitized = sanitized.trimStart();

    // 2) Normalize <html> wrapper
    sanitized = sanitized.replace(/^<html>\s*/i, "<html>");
    sanitized = sanitized.replace(/\s*<\/html>$/i, "</html>");

    // 3) Trim whitespace inside <p>
    sanitized = sanitized.replace(
      /<p[^>]*>\s*([\s\S]*?)\s*<\/p>/gi,
      (_m, inner) => `<p>${inner.trim()}</p>`
    );

    // 4) Convert empty <p> into blank lines
    sanitized = sanitized.replace(/<p>\s*<\/p>/gi, "\n\n");

    // 6) Newline between consecutive headings
    sanitized = sanitized.replace(/<\/h([2-3])>(?=<h[2-3])/g, "</h$1>\n");

    // 7) Remove whitespace nodes
    sanitized = sanitized.replace(/>\s+</g, "><");

    // 8) Collapse newline runs
    sanitized = sanitized.replace(/\n{3,}/g, "\n\n");

    // 9) newline → <br>
    sanitized = sanitized.replace(/\n/g, "<br>\n");

    // 10) Remove leading <br>
    sanitized = sanitized.replace(/^(<br\s*\/?>\s*)+/i, "");

    // 11) Remove blank lines after headers
    sanitized = sanitized.replace(/<\/h([23])>\s*(<br\s*\/?>\s*)+/gi, "</h$1>");

    // ⭐ NEW: Insert one blank line before each heading except the first one
    let isFirstHeading = true;
    sanitized = sanitized.replace(/(<h[23]>)/gi, (match, p1) => {
      if (isFirstHeading) {
        isFirstHeading = false;
        return p1;
      }
      return "<br>" + p1;
    });

    // 12) Collapse all <br> runs to max 2
    sanitized = sanitized.replace(/(<br\s*\/?>\s*){2,}/gi, "<br><br>");

    // 13) Cleanup header blank lines again if needed
    sanitized = sanitized.replace(/<\/h([23])>\s*(<br\s*\/?>\s*)+/gi, "</h$1>");

    return sanitized;
  };

  // NOTE: V1 startSession and stopSession functions removed - use V2 startOrResumeSessionV2 and pauseCurrentSegmentV2

  // Helper function: Enforce sliding window across all transcript_segments_* keys
  // Keeps only the MAX_RECORDINGS_PER_KEY most recent segments based on latest timestamp
  // Excludes the current active segment from removal
  const enforceStorageKeyLimit = (
    currentVisitNoteId?: number,
    currentClinicId?: number,
    currentPatientId?: number
  ): void => {
    try {
      // Get current segment key to protect it from removal
      // Use provided IDs or fall back to currentSegmentData
      const clinicId = currentClinicId || currentSegmentData?.clinicId;
      const patientId = currentPatientId || currentSegmentData?.patientId;

      const currentSegmentKey =
        currentVisitNoteId && clinicId && patientId
          ? `transcript_segments_${clinicId}_${patientId}_${currentVisitNoteId}`
          : null;

      // 1. Find all transcript_segments_* keys in localStorage
      const allKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("transcript_segments_")) {
          allKeys.push(key);
        }
      }

      // 2. If we have MAX_RECORDINGS_PER_KEY or fewer keys, no need to clean up
      if (allKeys.length <= MAX_RECORDINGS_PER_KEY) {
        return;
      }

      // 3. Get the latest timestamp from each key
      const keyTimestamps: Array<{
        key: string;
        latestTimestamp: string;
        totalRecordings: number;
      }> = [];

      for (const key of allKeys) {
        try {
          const data = localStorage.getItem(key);
          if (!data) continue;

          const segments = JSON.parse(data);
          if (!Array.isArray(segments) || segments.length === 0) continue;

          // Get the latest timestamp (last item in the array)
          const lastSegment = segments[segments.length - 1];
          const latestTimestamp =
            lastSegment?.timestamp || new Date().toISOString();

          keyTimestamps.push({
            key,
            latestTimestamp,
            totalRecordings: segments.length,
          });
        } catch (parseError) {
          console.warn(
            `[HealthScribe Service] Failed to parse localStorage key ${key}, skipping:`,
            parseError
          );
        }
      }

      // 4. Sort by timestamp (oldest first)
      keyTimestamps.sort(
        (a, b) =>
          new Date(a.latestTimestamp).getTime() -
          new Date(b.latestTimestamp).getTime()
      );

      // 5. Remove oldest keys until we're at the limit (excluding current segment)
      let removedCount = 0;
      const targetRemovalCount = keyTimestamps.length - MAX_RECORDINGS_PER_KEY;

      if (targetRemovalCount > 0) {
        for (
          let i = 0;
          i < keyTimestamps.length && removedCount < targetRemovalCount;
          i++
        ) {
          const keyToRemove = keyTimestamps[i].key;

          // SKIP if this is the current active segment
          if (currentSegmentKey && keyToRemove === currentSegmentKey) {
            console.warn(
              `[HealthScribe Service] Skipped removal of current segment: ${keyToRemove}`
            );
            continue;
          }

          // Parse key to extract visitNoteId for logging
          const keyParts = keyToRemove.split("_");
          const visitNoteId = keyParts[4] || "?";

          localStorage.removeItem(keyToRemove);
          removedCount++;

          console.warn(
            `[HealthScribe Service] Removed old segment: VisitNote ${visitNoteId} (${keyTimestamps[i].totalRecordings} recordings)`
          );
        }

        if (removedCount > 0) {
          console.warn(
            `[HealthScribe Service] Sliding window: Removed ${removedCount} segment(s), kept ${MAX_RECORDINGS_PER_KEY} most recent`
          );
        }
      }
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to enforce storage key limit:",
        error
      );
    }
  };

  // Consolidated function: append optional new segment and load full transcript
  const appendAndLoadTranscriptions = (
    clinicId: number,
    patientId: number,
    newMessage: ITranscriptionMessage | null | undefined,
    visitNoteId: number
  ): ITranscriptionMessage[] => {
    try {
      // Always include visitNoteId in storage key (to separate transcripts by draft/note)
      const storageKey = `transcript_segments_${clinicId}_${patientId}_${visitNoteId}`;

      // 1. Load existing transcript from localStorage
      const existingData = localStorage.getItem(storageKey);
      let segments: ITranscriptionMessage[] = existingData
        ? JSON.parse(existingData)
        : [];

      // 2. If a new segment is provided (non-null) and it's final, merge and save
      if (newMessage && !newMessage.isPartial) {
        const newSegment: ITranscriptionMessage = {
          sessionId: newMessage.sessionId,
          timestamp: newMessage.timestamp || new Date().toISOString(),
          speaker: newMessage.speaker,
          message: newMessage.message,
          confidence: newMessage.confidence,
          isPartial: false,
        };

        // Use merge to avoid duplicates (handles race conditions with Heidi path)
        segments = mergeTranscriptions(segments, [newSegment]);

        // Save back to localStorage
        localStorage.setItem(storageKey, JSON.stringify(segments));

        // Sliding window mechanism: Enforce limit across ALL transcript_segments_* keys
        // This removes the oldest segment keys based on their latest timestamp
        // Pass visitNoteId, clinicId, and patientId to protect current segment from removal
        enforceStorageKeyLimit(visitNoteId, clinicId, patientId);
      }

      // 3. Return the full transcript as ITranscriptionMessage[]
      const transcriptions: ITranscriptionMessage[] = segments.map(
        (seg: any, index: number) => ({
          sessionId:
            currentSession?.sessionId ||
            currentSegmentData?.sessionId ||
            "unknown",
          speaker: seg.speaker || "talking",
          message: seg.message || "",
          timestamp: seg.timestamp || new Date().toISOString(),
          confidence: seg.confidence || 1.0,
          isPartial: seg.isPartial || false,
          sequenceNumber: index,
        })
      );

      return transcriptions;
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to append/load transcriptions:",
        error
      );
      return [];
    }
  };

  // Store the original controller handler to prevent handler chaining
  // This is captured once at initialization and never wrapped
  let originalControllerHandler:
    | ((message: ITranscriptionMessage) => void)
    | null = null;
  let transcriptionHandlerSetup = false;

  // Attempt to reconnect WebSocket periodically when in degraded mode
  const attemptReconnection = async (): Promise<void> => {
    const now = Date.now();

    // Throttle reconnection attempts
    if (now - lastReconnectAttempt < RECONNECT_INTERVAL_MS) {
      return;
    }

    // Only attempt if we have an active session
    const activeSessionId =
      currentSession?.sessionId || currentSegmentData?.sessionId;
    if (!activeSessionId || !isDegradedMode) {
      return;
    }

    lastReconnectAttempt = now;

    try {
      // Note: We don't have the websocket URL here - we need to get a fresh one from backend
      // The best approach is to let the existing recovery service handle full reconnection
      // This function serves as a placeholder for more sophisticated logic
      // For now, we rely on the buffer + flush mechanism when connection is restored externally
    } catch (error) {
      console.error(
        "[HealthScribe Service] Reconnection attempt failed:",
        error
      );
    }
  };

  // Start periodic reconnection attempts
  const startReconnectionAttempts = (): void => {
    if (reconnectIntervalId !== null) {
      return; // Already running
    }

    reconnectIntervalId = window.setInterval(() => {
      attemptReconnection();
    }, RECONNECT_INTERVAL_MS);
  };

  // Stop periodic reconnection attempts
  const stopReconnectionAttempts = (): void => {
    if (reconnectIntervalId !== null) {
      window.clearInterval(reconnectIntervalId);
      reconnectIntervalId = null;
    }
  };

  const connectLiveTranscription = async (
    sessionId: string,
    websocketUrl?: string
  ): Promise<IControllerPromiseResponse<void>> => {
    try {
      // Check for BOTH V1 currentSession and V2 currentSegmentData
      const isV1Session =
        currentSession && currentSession.sessionId === sessionId;
      const isV2Session =
        currentSegmentData && currentSegmentData.sessionId === sessionId;

      if (!isV1Session && !isV2Session) {
        throw new Error("Invalid session ID or no active session");
      }

      // Only set up the transcription handler once to avoid handler chaining that causes duplicate saves
      // Handler chaining bug: Each resume would wrap the previous handler, causing 2x, 3x, 4x duplicates
      if (!transcriptionHandlerSetup) {
        // Capture the ORIGINAL controller-level handler ONCE (set in healthScribeController.ts)
        // This prevents wrapping an already-wrapped handler on subsequent connects
        if (!originalControllerHandler) {
          originalControllerHandler =
            healthScribeWebSocketProvider.onTranscription;
        }

        // Set up our handler that adds to currentSegmentData and calls the ORIGINAL controller handler
        healthScribeWebSocketProvider.onTranscription = async (message) => {
          try {
            // V1: Update current session transcription array
            if (currentSession) {
              currentSession.transcription = currentSession.transcription || [];
              currentSession.transcription.push(message);
            }

            // V2: Update current segment transcriptions (format for backend)
            if (currentSegmentData) {
              const formattedMessage = {
                session_id: currentSegmentData.sessionId,
                speaker: message.speaker || "talking",
                message: message.message || "",
                confidence: message.confidence || 1.0,
                is_final: !message.isPartial,
                timestamp: message.timestamp || new Date().toISOString(),
              };
              currentSegmentData.transcriptions.push(formattedMessage);

              // REAL-TIME DATABASE SAVE: Save final transcriptions immediately
              // Includes automatic retry mechanism for previously failed saves
              // Only save if message is FINAL (not partial/incomplete)
              if (!message.isPartial) {
                // Include previously failed transcriptions for retry
                const transcriptionsToSave = [formattedMessage];

                // Add any previously failed transcriptions to this save attempt
                if (failedTranscriptionsBuffer.length > 0) {
                  transcriptionsToSave.unshift(...failedTranscriptionsBuffer);
                }

                // Use V2 batch save API with current + failed messages
                // Include same context that would be sent during pause operation
                healthScribeRepository
                  .batchSaveTranscriptionsV2({
                    clinic_id: currentSegmentData.clinicId || 0,
                    session_id: currentSegmentData.sessionId,
                    segment_id: currentSegmentData.segmentId,
                    visit_type: currentSegmentData.visitType || "START_OF_CARE",
                    transcriptions: transcriptionsToSave, // Current + previously failed
                    // Context is always {} during real-time saves - full context passed at generateNarrative time
                    context: {},
                    is_segment_completed: false, // Segment still active
                  })
                  .then(() => {
                    // Success! Clear the failed buffer since everything is now saved
                    if (failedTranscriptionsBuffer.length > 0) {
                      failedTranscriptionsBuffer = []; // Clear buffer on success
                    }
                  })
                  .catch((saveError) => {
                    // Save failed, buffer this transcription for retry
                    console.error(
                      "[HealthScribe Service] ⚠️ Failed to save real-time transcription (will retry with next save):",
                      saveError
                    );

                    // Add current message to failed buffer (don't add duplicates from retry)
                    if (
                      !failedTranscriptionsBuffer.some(
                        (t) => t.timestamp === formattedMessage.timestamp
                      )
                    ) {
                      if (
                        failedTranscriptionsBuffer.length <
                        MAX_FAILED_BUFFER_SIZE
                      ) {
                        failedTranscriptionsBuffer.push(formattedMessage);
                      } else {
                        console.error(
                          `[HealthScribe Service] ⚠️ Failed buffer full (${MAX_FAILED_BUFFER_SIZE}), dropping oldest`
                        );
                        failedTranscriptionsBuffer.shift(); // Remove oldest
                        failedTranscriptionsBuffer.push(formattedMessage);
                      }
                    }

                    // Transcription is still in memory and localStorage, so no permanent data loss
                    // It will be retried with the next successful save, or saved during pause/complete
                  });
              }
            }

            // Note: localStorage append+load will be handled by the UI's transcription handler
            // which has access to clinicId and patientId from Redux state

            // Call the ORIGINAL controller-level handler for Redux updates
            if (originalControllerHandler) {
              originalControllerHandler(message);
            }
          } catch (error) {
            console.error(
              "[HealthScribe Service] Failed to process transcription message:",
              error
            );
            // Still call the original controller handler even if processing fails
            if (originalControllerHandler) {
              originalControllerHandler(message);
            }
          }
        };

        transcriptionHandlerSetup = true;
      }

      // Connect to WebSocket with the provided URL
      if (!websocketUrl) {
        throw new Error("WebSocket URL is required for connection");
      }

      // Get patient ID from either V1 or V2 session
      const patientId = currentSession?.patientId || 0;

      await healthScribeWebSocketProvider.connect(
        sessionId,
        patientId as number,
        websocketUrl
      );

      // Connection successful - exit degraded mode
      if (isDegradedMode) {
        isDegradedMode = false;
      }

      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to connect live transcription:",
        error
      );
      return {
        success: false,
        error: `Failed to connect live transcription: ${error}`,
      };
    }
  };

  const disconnectLiveTranscription = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      await healthScribeWebSocketProvider.disconnect();
      transcriptionHandlerSetup = false; // Reset flag so handler can be set up on next connection

      // Note: We don't clear the buffer here because disconnect might be temporary (pause)
      // Buffer will be flushed when reconnected or cleared on stop

      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to disconnect live transcription:",
        error
      );
      return {
        success: false,
        error: `Failed to disconnect live transcription: ${error}`,
      };
    }
  };

  const requestMicrophonePermission = async (): Promise<
    IControllerPromiseResponse<{ granted: boolean }>
  > => {
    try {
      const result =
        await healthScribeAudioProvider.requestMicrophonePermission();

      return { success: true, data: result };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to request microphone permission:",
        error
      );
      return {
        success: false,
        error: `Failed to request microphone permission: ${error}`,
      };
    }
  };

  const startAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      // Check for BOTH V1 currentSession and V2 currentSegmentData
      if (!currentSession && !currentSegmentData) {
        const errorMsg = "No active session. Start a session first.";
        console.error("[HealthScribe Service] " + errorMsg, {
          hasCurrentSession: !!currentSession,
          hasCurrentSegmentData: !!currentSegmentData,
          timestamp: new Date().toISOString(),
        });

        // Return error (UI can detect this specific error via string matching)
        return {
          success: false,
          error:
            errorMsg +
            " Please ensure startOrResumeRecording is called before startAudioRecording.",
        };
      }

      // Check if audio is already recording (Heidi mode starts it in controller)
      // If already recording, skip WebSocket callback setup (not needed for Heidi mode)
      const wasAlreadyRecording = healthScribeAudioProvider.isRecording();

      // Start audio recording (idempotent - returns success if already recording)
      // Explicitly pass "aws" mode for 16kHz sample rate required by AWS Transcribe Medical
      // Note: Heidi mode (44.1kHz) is set in healthScribeController.ts when transcriptionSource === "heidi"
      await healthScribeAudioProvider.startRecording("aws");

      // Only set up WebSocket handlers if we weren't already recording (AWS mode)
      // In Heidi mode, the controller starts recording directly and we don't need WebSocket
      if (wasAlreadyRecording) {
        return { success: true };
      }

      // Set up audio chunk handler to send to WebSocket for live transcription
      healthScribeAudioProvider.onAudioChunk = async (chunk: IAudioChunk) => {
        try {
          if (healthScribeWebSocketProvider.isConnected()) {
            // WebSocket connected - send current chunk
            await healthScribeWebSocketProvider.sendAudioChunk(chunk);

            // If we have buffered chunks and not already flushing, flush them now
            if (audioChunkBuffer.length > 0 && !isFlushingBuffer) {
              isFlushingBuffer = true;

              try {
                // Flush buffered chunks
                for (const bufferedChunk of audioChunkBuffer) {
                  await healthScribeWebSocketProvider.sendAudioChunk(
                    bufferedChunk
                  );
                }

                // Clear buffer after successful flush
                audioChunkBuffer = [];
                isDegradedMode = false; // Exit degraded mode
              } catch (flushError) {
                console.error(
                  "[HealthScribe Service] ❌ Failed to flush buffer:",
                  flushError
                );
                // Keep buffer for next attempt
              } finally {
                isFlushingBuffer = false;
              }
            }
          } else {
            // WebSocket NOT connected - buffer the chunk (degraded mode)
            if (!isDegradedMode) {
              console.warn(
                "[HealthScribe Service] ⚠️ Entering DEGRADED MODE - buffering audio chunks"
              );
              isDegradedMode = true;
            }

            if (audioChunkBuffer.length < MAX_BUFFER_SIZE) {
              audioChunkBuffer.push(chunk);
            } else {
              // Buffer full - drop oldest chunk to make room
              console.warn(
                `[HealthScribe Service] Audio buffer full (${MAX_BUFFER_SIZE} chunks), dropping oldest chunk`
              );
              audioChunkBuffer.shift(); // Remove oldest
              audioChunkBuffer.push(chunk); // Add newest
            }
          }
        } catch (error) {
          console.error(
            "[HealthScribe Service] Failed to process audio chunk:",
            error
          );

          // Notify audio provider's error handler if available (will propagate to recovery service)
          if (healthScribeAudioProvider.onError) {
            healthScribeAudioProvider.onError(
              `Service layer failed to process audio chunk: ${error}`
            );
          }
        }
      };

      // Start periodic reconnection attempts (for degraded mode recovery)
      startReconnectionAttempts();

      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to start audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to start audio recording: ${error}`,
      };
    }
  };

  const stopAudioRecording = async (): Promise<
    IControllerPromiseResponse<{
      audioBlob: Blob;
      duration: number;
    }>
  > => {
    try {
      // Stop periodic reconnection attempts
      stopReconnectionAttempts();

      // Clear audio buffer
      if (audioChunkBuffer.length > 0) {
        console.warn(
          `⏸️ [Service] Discarding ${audioChunkBuffer.length} buffered audio chunks on stop`
        );
        audioChunkBuffer = [];
      }

      // Reset degraded mode flag
      isDegradedMode = false;

      // Stop audio recording and get the final audio data
      const audioResult = await healthScribeAudioProvider.stopRecording();

      return { success: true, data: audioResult };
    } catch (error) {
      console.error("⏸️ [Service] Failed to stop audio recording:", error);
      return {
        success: false,
        error: `Failed to stop audio recording: ${error}`,
      };
    }
  };

  const pauseAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      await healthScribeAudioProvider.pauseRecording();

      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to pause audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to pause audio recording: ${error}`,
      };
    }
  };

  const resumeAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      await healthScribeAudioProvider.resumeRecording();

      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to resume audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to resume audio recording: ${error}`,
      };
    }
  };

  const getSession = (): IHealthScribeSession | null => {
    return currentSession;
  };

  const isAudioRecording = (): boolean => {
    return healthScribeAudioProvider.isRecording();
  };

  const isAudioPaused = (): boolean => {
    return healthScribeAudioProvider.isPaused();
  };

  const hasActiveSession = (): boolean => {
    return !!(currentSession || currentSegmentData);
  };

  const getActiveSessionInfo = (): {
    type: "V1" | "V2";
    sessionId: string;
    segmentId?: string;
  } | null => {
    if (currentSegmentData) {
      return {
        type: "V2",
        sessionId: currentSegmentData.sessionId,
        segmentId: currentSegmentData.segmentId,
      };
    }
    if (currentSession) {
      return {
        type: "V1",
        sessionId: currentSession.sessionId,
      };
    }
    return null;
  };

  const populateSessionFromTranscription = (transcriptionData: {
    session_id: string;
    patient_id: string | number;
    clinic_id?: number;
    patient_name?: string;
    visit_type?: string;
    created_at?: string;
    transcription?: string;
    status?: SessionStatus;
  }): IHealthScribeSession => {
    // Parse transcription data if it's a string
    const parsedTranscription = transcriptionData.transcription
      ? JSON.parse(transcriptionData.transcription)
      : [];

    // Create session object from transcription data
    const session: IHealthScribeSession = {
      sessionId: transcriptionData.session_id,
      patientId: transcriptionData.patient_id,
      clinicId: transcriptionData.clinic_id || 0,
      patientName: transcriptionData.patient_name,
      visitType: transcriptionData.visit_type || "START_OF_CARE",
      startTime: transcriptionData.created_at || new Date().toISOString(),
      status: transcriptionData.status || "completed",
      transcription: parsedTranscription,
    };

    // Set as current session
    currentSession = session;

    return session;
  };

  const uploadCompleteAudio = async (
    request: IS3UploadCompleteAudioRequest
  ): Promise<IControllerPromiseResponse<IS3UploadCompleteAudioResponse>> => {
    try {
      const response = await healthScribeRepository.uploadCompleteAudio(
        request
      );

      // Check if response indicates an error (404, 400, etc.)
      // The apiProvider returns error responses as data instead of throwing
      const isError =
        (response as any)?.detail?.message || // Backend error format
        (response as any)?.error || // Generic error field
        (response as any)?.status === "error" || // Status field
        (!response?.upload_url && !(response as any)?.success); // Missing expected success fields

      if (isError) {
        console.error(
          "[HealthScribe Service] ⚠️ Audio upload failed (non-critical - training data only):",
          {
            sessionId: request.session_id,
            filename: request.filename,
            errorResponse: response,
            note: "Continuing without audio - transcriptions are saved separately",
          }
        );
        return {
          success: false,
          error:
            (response as any)?.detail?.message ||
            (response as any)?.error ||
            "Upload failed",
        };
      }

      return { success: true, data: response };
    } catch (error) {
      console.error(
        "[HealthScribe Service] ⚠️ Audio upload failed (non-critical - training data only):",
        {
          sessionId: request.session_id,
          filename: request.filename,
          error: error,
          note: "Continuing without audio - transcriptions are saved separately",
        }
      );
      return {
        success: false,
        error: `Failed to upload complete audio: ${error}`,
      };
    }
  };

  const listTranscriptions = async (
    request: IListTranscriptionsRequest
  ): Promise<IControllerPromiseResponse<IListTranscriptionsResponse>> => {
    try {
      const response = await healthScribeRepository.listTranscriptions(request);

      return { success: true, data: response };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to list transcriptions:",
        error
      );
      return {
        success: false,
        error: `Failed to list transcriptions: ${error}`,
      };
    }
  };

  // V1 completeTranscriptionGenerateNarrative removed - use V2 generateNarrative

  // ==================== V2 Multi-Segment Service Methods ====================

  // Create backend session without starting audio/WebSocket (for localStorage recovery)
  const createSessionWithoutRecording = async (
    request: IStartRecordingV2Request
  ): Promise<IControllerPromiseResponse<{ session_id: string }>> => {
    try {
      // Only create the backend session record - no audio, no WebSocket, no currentSegmentData
      const response = await healthScribeRepository.startRecordingV2(request);

      // Return ONLY the session ID - no state changes
      return { success: true, data: { session_id: response.session_id } };
    } catch (error) {
      console.error(
        "[HealthScribe Service V2] Failed to create session:",
        error
      );
      return {
        success: false,
        error: `Failed to create session: ${error}`,
      };
    }
  };

  const startOrResumeSessionV2 = async (
    request: IStartRecordingV2Request,
    visitNoteId?: number // For localStorage fallback saves
  ): Promise<IControllerPromiseResponse<IStartRecordingV2Response>> => {
    try {
      // SEGMENT CREATION:
      // This function ALWAYS creates a NEW segment:
      // - If resume_session_id is null → New session + Segment #1
      // - If resume_session_id exists → Same session + Segment #(n+1)
      // Each call represents a new recording period (pause/resume creates new segments)
      const response = await healthScribeRepository.startRecordingV2(request);

      // DEFENSIVE: Validate response has required fields
      if (!response.session_id || !response.segment_id) {
        throw new Error(
          `Invalid backend response: missing session_id or segment_id`
        );
      }

      // DEFENSIVE: Validate segment_number is positive integer
      if (
        !response.segment_number ||
        response.segment_number < 1 ||
        !Number.isInteger(response.segment_number)
      ) {
        console.warn(
          `[HealthScribe Service V2] Invalid segment_number: ${response.segment_number}, defaulting to 1`
        );
        response.segment_number = 1;
      }

      // Safety net: Handle stale failed transcriptions from previous segment
      // Normal flow: pause saves them to correct segment_id, this should be empty
      // Edge case: If pause failed or was skipped, these belong to OLD segment
      //            and cannot be saved to the NEW segment (wrong segment_id)
      if (failedTranscriptionsBuffer.length > 0) {
        // Last-ditch save to localStorage before clearing
        // Use old segment's data if available, otherwise use new request's data
        const fallbackClinicId =
          currentSegmentData?.clinicId || request.clinic_id;
        const fallbackPatientId =
          currentSegmentData?.patientId || request.patient_id;
        const fallbackVisitNoteId =
          currentSegmentData?.visitNoteId || visitNoteId;

        if (fallbackClinicId && fallbackPatientId && fallbackVisitNoteId) {
          console.warn(
            `[HealthScribe Service V2] Saving ${failedTranscriptionsBuffer.length} stale failed transcription(s) to localStorage before clearing`
          );
          for (const trans of failedTranscriptionsBuffer) {
            // Convert backend format to ITranscriptionMessage format for localStorage
            appendAndLoadTranscriptions(
              fallbackClinicId,
              fallbackPatientId,
              {
                sessionId:
                  trans.session_id || currentSegmentData?.sessionId || "",
                timestamp: trans.timestamp,
                speaker: trans.speaker || "talking",
                message: trans.message,
                confidence: trans.confidence,
                isPartial: false, // These are final transcriptions
              },
              fallbackVisitNoteId
            );
          }
        } else {
          console.warn(
            `[HealthScribe Service V2] Cannot save ${failedTranscriptionsBuffer.length} stale failed transcription(s) to localStorage - missing clinicId/patientId/visitNoteId`
          );
        }
        failedTranscriptionsBuffer = [];
      }

      // Store current segment info (including visitNoteId for future fallback saves)
      currentSegmentData = {
        sessionId: response.session_id,
        segmentId: response.segment_id,
        segmentNumber: response.segment_number,
        transcriptions: [],
        clinicId: request.clinic_id,
        patientId: request.patient_id,
        visitNoteId: visitNoteId, // Store for future fallback saves
        visitType: request.visit_type,
        context: undefined, // Not used here - full context passed at generateNarrative() time
      };

      return { success: true, data: response };
    } catch (error) {
      console.error(
        "[HealthScribe Service V2] Failed to start/resume session:",
        error
      );
      return {
        success: false,
        error: `Failed to start/resume session: ${error}`,
      };
    }
  };

  const pauseCurrentSegmentV2 = async (
    clinicId: number,
    _visitType: string,
    context?: ITranscriptionContext
  ): Promise<IControllerPromiseResponse<IStopRecordingV2Response>> => {
    try {
      // If no active segment, return error
      if (!currentSegmentData) {
        return {
          success: false,
          error: "No active segment to pause",
        };
      }

      // DEFENSIVE: Validate segment data integrity
      if (!currentSegmentData.sessionId || !currentSegmentData.segmentId) {
        console.error(
          "[HealthScribe Service V2] Corrupt segment data:",
          currentSegmentData
        );
        throw new Error(
          "Corrupt segment data: missing session_id or segment_id"
        );
      }

      // TODO: Consider removing this context parameter entirely:
      // 1. Unreliable: Only runs if user gracefully pauses (not force-close)
      // 2. Unused: We now send context: {} to batch saves, generateNarrative() passes fresh context
      // 3. Backend confirmed context is required but {} (empty) is valid
      if (context) {
        currentSegmentData.context = context;
      }

      // SEGMENT CLOSING - PAUSE MODE:
      // - Stops current segment (closes this recording period)
      // - Sets session status to "paused" (can resume later)
      // - User can click "Resume" to create a NEW segment (segment #n+1)
      // - AWS keeps billing while paused (session still active)

      // Stop segment with "pause" action
      const stopResponse = await healthScribeRepository.stopRecordingV2({
        session_id: currentSegmentData.sessionId,
        segment_id: currentSegmentData.segmentId,
        clinic_id: clinicId,
        action: "pause",
      });

      // Final attempt to save any failed transcriptions before closing segment
      // This is our last chance to save to the correct segment_id before it's gone
      if (failedTranscriptionsBuffer.length > 0 && currentSegmentData) {
        try {
          await healthScribeRepository.batchSaveTranscriptionsV2({
            clinic_id: currentSegmentData.clinicId || clinicId,
            session_id: currentSegmentData.sessionId,
            segment_id: currentSegmentData.segmentId,
            visit_type: currentSegmentData.visitType || "START_OF_CARE",
            transcriptions: failedTranscriptionsBuffer,
            // Context is {} here - full context passed at generateNarrative time
            context: {},
            is_segment_completed: true, // Segment is closing
          });
          // Success! Clear the buffer
          failedTranscriptionsBuffer = [];
        } catch (finalSaveError) {
          // Final save failed - transcriptions are still in localStorage as backup
          console.warn(
            `[HealthScribe Service V2] ⚠️ Final batch save of ${failedTranscriptionsBuffer.length} failed transcription(s) failed:`,
            finalSaveError
          );
          // Clear buffer anyway - data is in localStorage, will be merged during generateNarrative
          failedTranscriptionsBuffer = [];
        }
      }

      // Clear segment data after stop API succeeds
      currentSegmentData = null;

      return { success: true, data: stopResponse };
    } catch (error) {
      console.error(
        "[HealthScribe Service V2] Failed to pause segment:",
        error
      );
      // Don't clear currentSegmentData on error - keep session active
      return { success: false, error: `Failed to pause segment: ${error}` };
    }
  };

  const forcePauseSegmentByIds = async ({
    clinicId,
    sessionId,
    segmentId,
  }: {
    clinicId: number;
    sessionId: string;
    segmentId: string;
  }): Promise<IControllerPromiseResponse<IStopRecordingV2Response>> => {
    try {
      const response = await healthScribeRepository.stopRecordingV2({
        session_id: sessionId,
        segment_id: segmentId,
        clinic_id: clinicId,
        action: "pause",
      });

      return { success: true, data: response };
    } catch (error) {
      console.error(
        "[HealthScribe Service V2] Failed to force pause remote segment:",
        error
      );
      return {
        success: false,
        error: `Failed to pause remote segment: ${error}`,
      };
    }
  };

  // Stop segment (used before deleting draft or saving visit)
  // Changed from "complete" to "pause" to keep sessions resumable
  const stopAndCompleteSegmentV2 = async (
    clinicId: number
  ): Promise<IControllerPromiseResponse<IStopRecordingV2Response>> => {
    try {
      // Idempotent: If no active segment (already stopped), return success
      if (!currentSegmentData) {
        return {
          success: true,
          data: {
            session_id: "already-stopped",
            segment_id: "already-stopped",
            segment_number: 0,
            segment_duration: 0,
            total_duration: 0,
            message_count: 0,
            audio_file_url: "",
            transcription_file_url: "",
            status: "paused",
            can_resume: true,
          },
        };
      }

      // DEFENSIVE: Validate segment data integrity
      if (!currentSegmentData.sessionId || !currentSegmentData.segmentId) {
        console.error(
          "[HealthScribe Service V2] Corrupt segment data:",
          currentSegmentData
        );
        throw new Error(
          "Corrupt segment data: missing session_id or segment_id"
        );
      }

      // SEGMENT CLOSING - PAUSE MODE (changed from complete):
      // - Stops current segment (closes this recording period)
      // - Sets session status to "paused" (CAN resume later)
      // - Keeps session resumable for better UX
      // - No more "complete" action that blocks resumption

      // Stop segment with "pause" action (keeps session resumable)
      const stopResponse = await healthScribeRepository.stopRecordingV2({
        session_id: currentSegmentData.sessionId,
        segment_id: currentSegmentData.segmentId,
        clinic_id: clinicId,
        action: "pause", // ✅ Changed from "complete" - keeps session resumable
      });

      // 🔥 REMOVED BATCH SAVE: Transcriptions are now saved in real-time as they arrive
      // No need to save again on stop - this was causing duplicates

      // Clear segment data after stop API succeeds

      currentSegmentData = null;

      return { success: true, data: stopResponse };
    } catch (error) {
      console.error("[HealthScribe Service V2] Failed to stop segment:", error);
      // Don't clear currentSegmentData on error - keep session active
      return {
        success: false,
        error: `Failed to stop segment: ${error}`,
      };
    }
  };

  // Generate narrative from session transcriptions (does NOT mark session as "completed")
  // Session is marked as "completed" separately via markSessionAsCompletedV2 when user saves the visit
  const generateNarrative = async (
    clinicId: number,
    visitType: string,
    context: ITranscriptionContext,
    visitNoteId: number, // REQUIRED: for keying localStorage by visit note
    sessionId?: string, // Optional: for paused sessions without active recording
    patientId?: number // Optional: for loading complete transcript from localStorage
  ): Promise<IControllerPromiseResponse<IGenerateNarrativeV2Response>> => {
    try {
      let targetSessionId: string;

      // Scenario 1: Generating from a paused session (no active recording)
      if (sessionId && !currentSegmentData) {
        targetSessionId = sessionId;

        // Step 1: Load transcripts from localStorage (may be partial or complete)
        let localTranscriptions: ITranscriptionMessage[] = [];
        if (patientId) {
          localTranscriptions = appendAndLoadTranscriptions(
            clinicId,
            patientId,
            null, // Just load, don't append
            visitNoteId
          );
        }

        // Step 2: Always fetch from backend to get any transcriptions we might have missed
        // This handles: multi-device flow, failed saves that recovered, partial recordings, etc.
        const backendTranscriptions: ITranscriptionMessage[] = [];
        try {
          const sessionData = await healthScribeRepository.getSessionV2({
            session_id: targetSessionId,
            clinic_id: clinicId,
          });

          // Extract transcriptions from all segments
          if (sessionData?.segments && sessionData.segments.length > 0) {
            for (const segment of sessionData.segments) {
              if (segment.transcriptions && segment.transcriptions.length > 0) {
                // Convert UNKNOWN speaker to "talking" for display consistency
                const normalizedTranscriptions = segment.transcriptions.map(
                  (t: any) => ({
                    ...t,
                    speaker: t.speaker || "talking",
                  })
                );
                backendTranscriptions.push(...normalizedTranscriptions);
              }
            }
          }
        } catch (backendError) {
          console.error(
            "[HealthScribe Service V2] Failed to fetch transcriptions from backend:",
            backendError
          );
          // Continue with localStorage only
        }

        // Step 3: Merge localStorage (older) with backend (newer)
        // This ensures we get ALL transcriptions even if some failed to save or user switched devices
        const completeTranscript = mergeTranscriptions(
          localTranscriptions,
          backendTranscriptions
        );

        // Log warning if still no transcriptions
        if (completeTranscript.length === 0) {
          console.warn(
            "[HealthScribe Service V2] ⚠️ No transcriptions found in localStorage or backend. Narrative will be generated from context only."
          );
        }

        context = {
          ...context,
          transcriptions: completeTranscript.map(
            (transcription) => transcription.message
          ),
        };

        // Generate narrative
        const narrativeResponse =
          await healthScribeRepository.generateNarrativeV2({
            session_id: targetSessionId,
            clinic_id: clinicId,
            context: context,
            template_id: context.template_id,
          });

        const sanitizedNarrative =
          sanitizeNarrative(narrativeResponse?.narrative) ?? undefined;
        if (sanitizedNarrative !== undefined) {
          narrativeResponse.narrative = sanitizedNarrative;
        }

        return { success: true, data: narrativeResponse };
      }

      // Error case: Neither paused session nor active recording
      if (!currentSegmentData) {
        throw new Error(
          "No active segment and no session ID provided for narrative generation"
        );
      }

      // This should never happen - UI requires pause before generate narrative
      throw new Error(
        "Unexpected state: User somehow clicked Generate Narrative during an active recording session, even though the button is disabled. Please pause first."
      );
    } catch (error) {
      console.error(
        "[HealthScribe Service V2] Failed to generate narrative:",
        error
      );
      // Don't clear currentSegmentData on error - keep session active
      return {
        success: false,
        error: `Failed to generate narrative: ${error}`,
      };
    }
  };

  const addTranscriptionToCurrentSegment = (transcription: any) => {
    if (currentSegmentData) {
      currentSegmentData.transcriptions.push(transcription);
    }
  };

  const getCurrentSegmentData = () => {
    return currentSegmentData;
  };

  // Update context for current segment (called from UI when context changes)
  const updateSegmentContext = (context: ITranscriptionContext) => {
    if (currentSegmentData) {
      currentSegmentData.context = context;
    } else {
      console.warn(
        "[HealthScribe Service] Cannot update context: No active segment"
      );
    }
  };

  const markSessionAsCompletedV2 = async (
    request: ICompleteSessionV2Request
  ): Promise<IControllerPromiseResponse<ICompleteSessionV2Response>> => {
    try {
      const response = await healthScribeRepository.completeSessionV2(request);

      return { success: true, data: response };
    } catch (error) {
      console.error(
        "[HealthScribe Service V2] Failed to mark session as completed:",
        error
      );
      return {
        success: false,
        error: `Failed to mark session as completed: ${error}`,
      };
    }
  };

  const listSessionsV2 = async (
    request: IListSessionsV2Request
  ): Promise<IListSessionsV2Response> => {
    try {
      const response = await healthScribeRepository.listSessionsV2(request);

      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Service V2] Failed to list sessions:",
        error
      );
      throw error;
    }
  };

  // Clear transcriptions from localStorage for a given clinic/patient
  const clearTranscriptionsFromLocalStorage = (
    clinicId: number,
    patientId: number,
    visitNoteId: number
  ): void => {
    try {
      const storageKey = `transcript_segments_${clinicId}_${patientId}_${visitNoteId}`;
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to clear transcriptions from localStorage:",
        error
      );
    }
  };

  // Upload localStorage transcripts to a new session (for multi-session workflow)
  const syncLocalStorageToNewSession = async (
    clinicId: number,
    patientId: number,
    sessionId: string,
    segmentId: string,
    visitType: string,
    visitNoteId: number
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // Load all transcripts from localStorage
      const completeTranscript = appendAndLoadTranscriptions(
        clinicId,
        patientId,
        null, // Just load, don't append
        visitNoteId
      );

      if (completeTranscript.length === 0) {
        return { success: true };
      }

      // Format for backend
      const backendTranscriptions = completeTranscript.map((msg: any) => ({
        session_id: sessionId,
        speaker: msg.speaker || "talking",
        message: msg.message,
        confidence: msg.confidence || 1.0,
        is_final: true,
        timestamp: msg.timestamp || new Date().toISOString(),
      }));

      // Upload to backend
      await healthScribeRepository.batchSaveTranscriptionsV2({
        clinic_id: clinicId,
        session_id: sessionId,
        segment_id: segmentId,
        visit_type: visitType,
        transcriptions: backendTranscriptions,
        context: {}, // Full context passed at generateNarrative() time
        is_segment_completed: false, // Segment is still active
      });

      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Service V2] ⚠️ Failed to sync localStorage transcripts:",
        error
      );
      return {
        success: false,
        error: `Failed to sync transcripts: ${error}`,
      };
    }
  };

  // ==================== End V2 Methods ====================

  return {
    // Shared Methods (used by both V1 legacy and V2)
    connectLiveTranscription,
    disconnectLiveTranscription,
    requestMicrophonePermission,
    startAudioRecording,
    stopAudioRecording,
    pauseAudioRecording,
    resumeAudioRecording,
    getSession,
    populateSessionFromTranscription,
    uploadCompleteAudio,
    listTranscriptions,
    // V2 Methods
    startOrResumeSessionV2,
    createSessionWithoutRecording, // For localStorage recovery without audio/WebSocket overhead
    pauseCurrentSegmentV2, // Stops segment with action="pause" (session stays active, AWS keeps billing)
    forcePauseSegmentByIds,
    stopAndCompleteSegmentV2, // Stops segment with action="complete" (session completed, AWS billing stops)
    generateNarrative, // Generate narrative from transcriptions (does NOT mark session as "completed")
    markSessionAsCompletedV2, // Mark session as "completed" (called when user saves visit)
    addTranscriptionToCurrentSegment,
    getCurrentSegmentData,
    updateSegmentContext, // Update context for real-time database saves
    listSessionsV2,
    appendAndLoadTranscriptions,
    clearTranscriptionsFromLocalStorage,
    syncLocalStorageToNewSession,
    enforceStorageKeyLimit, // Sliding window enforcement across all transcript keys
    // Audio State Helpers
    isAudioRecording,
    isAudioPaused,
    // Session Verification Helpers
    hasActiveSession,
    getActiveSessionInfo,
  };
};
